import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Compass } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { OtherPagesHeader } from "@/components/about/OtherPagesHeader";
import { AboutFaqSection } from "@/components/about/AboutFaqSection";
import { AboutFooter } from "@/components/about/AboutFooter";
import { FloatingAboutDock } from "@/components/about/FloatingAboutDock";
import { ThemeDialog } from "@/components/theme/ThemeDialog";
import MobileBottomNav from "@/components/dashboard/MobileBottomNav";
import { cn } from "@/lib/utils";

const linkItems: {
  title: string;
  description: string;
  to: string;
}[] = [
  {
    title: "Metefy QR code stickers",
    description:
      "Stick a Metefy QR on your things—if they’re lost, a finder scans and reaches you in one step.",
    to: "/",
  },
  {
    title: "3D map–based proptech platform",
    description:
      "Map-first property discovery: listings anchored to real-world coordinates—pan, zoom, and explore nearby properties spatially.",
    to: "/found-items-map",
  },
  {
    title: "Bulk customizable QR code generator",
    description: "Create free custom QR codes in bulk for your links and campaigns.",
    to: "/free-custom-bulk-qr-code-generator",
  },
  {
    title: "3D sticker store",
    description: "Browse and shop 3D-style sticker packs and designs.",
    to: "/sticker-store",
  },
  {
    title: "3D interactive flipbook",
    description: "Flip through Metefy and the QR generator in 3D.",
    to: "/flipbook",
  },
  {
    title: "3D map animations",
    description: "Cinematic map motion and highlights across the globe.",
    to: "/map-animations",
  },
];

export default function Links() {
  const { user } = useAuth();
  const [isThemeDialogOpen, setIsThemeDialogOpen] = useState(false);

  return (
    <>
      <Helmet>
        <title>Explore Metefy — Links to QR, map, stickers & more</title>
        <meta
          name="description"
          content="Choose where to go next: Metefy home & QR stickers, 3D proptech map, bulk QR generator, sticker store, flipbook, and map animations."
        />
        <link rel="canonical" href="https://www.metefy.com/links" />
        <meta property="og:title" content="Explore Metefy — Quick links" />
        <meta
          property="og:description"
          content="Jump to the home page, 3D map, QR tools, sticker store, flipbook, and animations."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.metefy.com/links" />
        <meta property="og:image" content="https://www.metefy.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <OtherPagesHeader />

        <main
          className={`flex-1 pt-24 sm:pt-28 pb-20 px-4 sm:px-6 ${user ? "pb-28 md:pb-20" : ""}`}
          role="main"
        >
          <div className="max-w-3xl mx-auto text-center mb-12 sm:mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-card text-sm text-foreground">
              <Compass className="w-4 h-4 text-primary shrink-0" aria-hidden />
              <span>Explore</span>
            </div>
            <h1 className="mt-6 text-3xl sm:text-4xl md:text-5xl font-bold text-foreground tracking-tight">
              What do you want to <span className="text-primary">explore</span>?
            </h1>
            <p className="mt-4 text-base sm:text-lg text-foreground/85 max-w-lg mx-auto leading-relaxed">
              Pick a destination — each link opens the full page experience.
            </p>
          </div>

          <div className="max-w-4xl mx-auto grid gap-4 sm:grid-cols-2">
            {linkItems.map(({ title, description, to }, i) => (
              <Link
                key={`${to}-${title}`}
                to={to}
                className={cn(
                  "group rounded-xl border border-border bg-card p-5 sm:p-6 text-left",
                  "transition-colors duration-200",
                  "hover:border-primary/40 hover:bg-card",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                )}
              >
                <h2 className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-base sm:text-[1.05rem] font-semibold text-foreground leading-snug group-hover:text-primary transition-colors">
                  <span className="text-[11px] font-mono font-normal tabular-nums tracking-widest text-foreground/45 shrink-0">
                    {String(i + 1).padStart(2, "0")}.
                  </span>
                  <span className="min-w-0">{title}</span>
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground">
                  {description}
                </p>
                <p className="mt-4 text-sm font-medium text-primary inline-flex items-center gap-1">
                  Continue
                  <span className="transition-transform group-hover:translate-x-0.5" aria-hidden>
                    →
                  </span>
                </p>
              </Link>
            ))}
          </div>
        </main>

        <AboutFaqSection />
        <AboutFooter />

        {user && <MobileBottomNav />}
        <FloatingAboutDock onOpenThemeDialog={() => setIsThemeDialogOpen(true)} />
        <ThemeDialog isOpen={isThemeDialogOpen} setIsOpen={setIsThemeDialogOpen} />
      </div>
    </>
  );
}
